import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { ERequestType } from "../../Enum/ERequestType"
import type { IPlatformWhatsAppPairingResult } from "../../Objects/PlatformWhatsApp/IPlatformWhatsAppPairingResult"
import type { IPlatformWhatsAppKnowledge } from "../../Objects/PlatformWhatsApp/IPlatformWhatsAppKnowledge"
import type { IPlatformWhatsAppMessage } from "../../Objects/PlatformWhatsApp/IPlatformWhatsAppMessage"
import type { IPlatformWhatsAppConversation } from "../../Objects/PlatformWhatsApp/IPlatformWhatsAppConversation"
import type { IPlatformWhatsAppSettings } from "../../Objects/PlatformWhatsApp/IPlatformWhatsAppSettings"
import type { IPlatformWhatsAppOverview } from "../../Objects/PlatformWhatsApp/IPlatformWhatsAppOverview"
import { DeleteRequest, GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"

export const usePlatformWhatsApp = () => {
  const { t } = useTranslation("platformWhatsApp")

  const { setError } = useError()

  const { enableLoad, disableLoad } = useLoad()

  const execute = async <T,>(
    operation: () => Promise<{ success: boolean; message?: string; data?: T }>,
    silent = false,
  ) => {
    if (!silent) enableLoad()
    try {
      const response = await operation()

      if (!response.success) throw new Error(response.message || t("errors.default"))

      return response.data
    } catch (error: unknown) {
      if (!silent) setError(t("title"), error instanceof Error ? error.message : t("errors.default"))

      throw error
    } finally {
      if (!silent) disableLoad()
    }
  }

  const getOverview = (silent = false) =>
    execute<IPlatformWhatsAppOverview>(
      () => GetRequest(API_ROUTES.PLATFORM_WHATSAPP.OVERVIEW, ERequestType.Admin),
      silent,
    )

  const getSettings = () =>
    execute<IPlatformWhatsAppSettings>(() => GetRequest(API_ROUTES.PLATFORM_WHATSAPP.SETTINGS, ERequestType.Admin))

  const saveSettings = (settings: IPlatformWhatsAppSettings) =>
    execute<IPlatformWhatsAppSettings>(() =>
      PutRequest(API_ROUTES.PLATFORM_WHATSAPP.SETTINGS, settings, ERequestType.Admin),
    )

  const generatePairingCode = (agentName: string) =>
    execute<IPlatformWhatsAppPairingResult>(() =>
      PostRequest(API_ROUTES.PLATFORM_WHATSAPP.GENERATE_PAIRING_CODE, { agentName }, ERequestType.Admin),
    )

  const deleteAgent = (agentId: number) =>
    execute(() => DeleteRequest(API_ROUTES.PLATFORM_WHATSAPP.DELETE_AGENT(agentId), ERequestType.Admin))

  const getConversations = (silent = false) =>
    execute<IPlatformWhatsAppConversation[]>(
      () => GetRequest(API_ROUTES.PLATFORM_WHATSAPP.CONVERSATIONS, ERequestType.Admin),
      silent,
    )

  const getMessages = (conversationId: number, silent = false) =>
    execute<IPlatformWhatsAppMessage[]>(
      () => GetRequest(API_ROUTES.PLATFORM_WHATSAPP.MESSAGES(conversationId), ERequestType.Admin),
      silent,
    )

  const sendMessage = (conversationId: number, body: string) =>
    execute<number>(() =>
      PostRequest(API_ROUTES.PLATFORM_WHATSAPP.MESSAGES(conversationId), { body }, ERequestType.Admin),
    )

  const updateConversation = (conversation: IPlatformWhatsAppConversation) =>
    execute(() =>
      PutRequest(
        API_ROUTES.PLATFORM_WHATSAPP.CONVERSATION(conversation.id),
        {
          stage: conversation.stage,
          status: conversation.status,
          leadScore: conversation.leadScore,
          summary: conversation.summary,
        },
        ERequestType.Admin,
      ),
    )

  const getKnowledge = () =>
    execute<IPlatformWhatsAppKnowledge[]>(() => GetRequest(API_ROUTES.PLATFORM_WHATSAPP.KNOWLEDGE, ERequestType.Admin))

  const saveKnowledge = (document: IPlatformWhatsAppKnowledge) =>
    execute<IPlatformWhatsAppKnowledge>(() =>
      PostRequest(API_ROUTES.PLATFORM_WHATSAPP.KNOWLEDGE, document, ERequestType.Admin),
    )

  const deleteKnowledge = (id: number) =>
    execute(() => DeleteRequest(API_ROUTES.PLATFORM_WHATSAPP.DELETE_KNOWLEDGE(id), ERequestType.Admin))

  return {
    getOverview,
    getSettings,
    saveSettings,
    generatePairingCode,
    deleteAgent,
    getConversations,
    getMessages,
    sendMessage,
    updateConversation,
    getKnowledge,
    saveKnowledge,
    deleteKnowledge,
  }
}
